/**
 * Manages the storage of web push notification messages.
 *
 * Also updates the app badge count.
 */
import Backbone from "backbone";

import readlangClient from "readlangClient";

interface Notification {
  _id: string;
  title: string;
  body: string;
}

/**
 * Returns all the stored notifications, including their keys.
 */
const getAll = async (): Promise<Notification[]> => {
  const data = (
    await readlangClient.request("GET", "/api/webPushNotifications", {})
  ).data;

  return data;
};

/** This model just reads data from the server, it doesn't write. */
class StoredNotificationsModel extends Backbone.Model<{
  notifications: Notification[];
}> {
  midnightTimerId: number | undefined = undefined;

  constructor() {
    super();
    this.set("notifications", []);
  }

  fetchAndSetup() {
    this.fetchNotificationsAndUpdateBadgeCount();

    // Re-fetch when the user returns to the tab
    document.addEventListener("visibilitychange", () => {
      if (window.document.visibilityState === "visible") {
        this.fetchNotificationsAndUpdateBadgeCount();
      }
    });

    // Listen for messages from the service worker
    navigator.serviceWorker?.addEventListener("message", (event) => {
      if (event.data.type === "PUSH_NOTIFICATION_RECEIVED") {
        // Process the notification data, display on the page, etc.
        this.fetchNotificationsAndUpdateBadgeCount();
      }
    });
  }

  updateMidnightTimer() {
    if (this.midnightTimerId) {
      clearTimeout(this.midnightTimerId);
    }

    // Re-fetch stored notifications at midnight local time since that's when they expire and we'll
    // want to reset the badge count.
    const now = new Date();
    const midnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0,
      0,
      0
    );
    const timeUntilMidnight = midnight.getTime() - now.getTime();

    this.midnightTimerId = setTimeout(
      () => this.fetchNotificationsAndUpdateBadgeCount(),
      timeUntilMidnight
    );
  }

  async fetchNotificationsAndUpdateBadgeCount() {
    const notifications = await getAll();

    // Filter out notifications from before today
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    navigator.setAppBadge?.(notifications.length > 0 ? 1 : 0);

    this.set("notifications", notifications);

    this.updateMidnightTimer();
  }

  fetch() {
    throw new Error(
      "fetch() not implemented. Use fetchNotifications() instead."
    );
    return null;
  }

  async deleteAllStoredNotifications() {
    const notifications = this.get("notifications");
    for (const notification of notifications) {
      await readlangClient.request(
        "DELETE",
        `/api/webPushNotifications/${notification._id}`,
        {}
      );
    }
    this.fetchNotificationsAndUpdateBadgeCount();
  }
}

export default StoredNotificationsModel;
